'use client';

import React, { useState, useEffect } from 'react';

interface Section {
  id: string;
  label: string;
  sectionId: string;
}

interface ProgressIndicatorProps {
  className?: string;
}

const ProgressIndicator = ({ className = '' }: ProgressIndicatorProps) => {
  const [activeSection, setActiveSection] = useState<string>('ecosystem');
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  const sections: Section[] = [
    { id: 'ecosystem', label: 'Ecosystem', sectionId: 'ecosystem-section' },
    { id: 'investment', label: 'Investment', sectionId: 'investment-section' },
    { id: 'roadmap', label: 'Roadmap', sectionId: 'roadmap-section' },
    { id: 'security', label: 'Security', sectionId: 'security-section' },
    { id: 'community', label: 'Community', sectionId: 'community-section' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(scrollPercentage, 100));
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const section = sections.find((s) => s.sectionId === sectionId);
          if (section) {
            setActiveSection(section.id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((section) => {
      const element = document.getElementById(section.sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleSectionClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <div data-component-id="src/components/common/ProgressIndicator.tsx:84:6" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="84" data-component-end-line="118" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`fixed left-8 top-1/2 -translate-y-1/2 z-[75] hidden lg:flex flex-col items-center gap-6 ${className}`}
      >
        <div data-component-id="src/components/common/ProgressIndicator.tsx:87:8" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="87" data-component-end-line="92" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-64%20w-1%20bg-muted%20rounded-full%20overflow-hidden%22%7D" className="relative h-64 w-1 bg-muted rounded-full overflow-hidden">
          <div data-component-id="src/components/common/ProgressIndicator.tsx:88:10" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="88" data-component-end-line="91" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20left-0%20w-full%20bg-accent%20transition-all%20duration-300%20ease-out%22%7D"
            className="absolute top-0 left-0 w-full bg-accent transition-all duration-300 ease-out"
            style={{ height: `${scrollProgress}%` }}
          />
        </div>

        <div data-component-id="src/components/common/ProgressIndicator.tsx:94:8" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="94" data-component-end-line="117" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-4%22%7D" className="flex flex-col gap-4">
          {sections.map((section, index) => (
            <button data-component-id="src/components/common/ProgressIndicator.tsx:96:12" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="96" data-component-end-line="115" data-component-file="ProgressIndicator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22group%20relative%20flex%20items-center%22%7D"
              key={section.id}
              onClick={() => handleSectionClick(section.sectionId)}
              className="group relative flex items-center"
              aria-label={`Navigate to ${section.label}`}
            >
              <div data-component-id="src/components/common/ProgressIndicator.tsx:102:14" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="102" data-component-end-line="107" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-accent border-accent scale-125' :'bg-transparent border-muted-foreground hover:border-accent'
                }`}
              />
              <span data-component-id="src/components/common/ProgressIndicator.tsx:108:14" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="108" data-component-end-line="114" data-component-file="ProgressIndicator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`absolute left-6 whitespace-nowrap font-space-mono text-xs transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                  activeSection === section.id ? 'text-accent' : 'text-text-secondary'
                }`}
              >
                {section.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div data-component-id="src/components/common/ProgressIndicator.tsx:120:6" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="120" data-component-end-line="125" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-0%20left-0%20right-0%20z-%5B75%5D%20lg%3Ahidden%20h-1%20bg-muted%22%7D" className="fixed bottom-0 left-0 right-0 z-[75] lg:hidden h-1 bg-muted">
        <div data-component-id="src/components/common/ProgressIndicator.tsx:121:8" data-component-path="src/components/common/ProgressIndicator.tsx" data-component-line="121" data-component-end-line="124" data-component-file="ProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20bg-accent%20transition-all%20duration-300%20ease-out%22%7D"
          className="h-full bg-accent transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
    </>
  );
};

export default ProgressIndicator;